// NOTE: Refer to https://momentjs.com/docs/#/manipulating/add/ for more info
import moment from 'moment';
import { Moment } from 'moment';
import { FORMAT } from './formats';

export enum MOMENT_UNIT {
    YEARS = 'y',
    QUARTERS = 'Q',
    MONTHS = 'M',
    WEEKS = 'w',
    DAYS = 'd',
    HOURS = 'h',
    MINUTES = 'm',
    SECONDS = 's',
    MILLISECONDS = 'ms',
}

export function momentToDate(dateMoment: Moment): Date {
    let result: Date = null;

    if (dateMoment && dateMoment.isValid()) {
        result = dateMoment.toDate();
    }

    return result;
}

export function momentToString(
    datetimeMoment: Moment,
    format: string = FORMAT.DATETIME_SERVER
): string {
    let result: string;

    if (datetimeMoment?.isValid && datetimeMoment?.isValid()) {
        result = datetimeMoment.format(format);
    }

    return result;
}

export function verifyMoment(value: any) {
    return moment.isMoment(value) && value.isValid ? value : moment(value);
}

export function isValidMoment(value: any) {
    return moment.isMoment(value) && value.isValid && value.isValid();
}

export function utcStringToMoment(
    value: string,
    format: string = FORMAT.DATETIME_SERVER
): Moment {
    return value && value.length ? moment.utc(value, format).local() : null;
}

export function momentsRelativeDifference(
    d1: Moment,
    d2: Moment
): { amount: number; label: string } {
    let keys: any = {
        today: 'Common.Today',
        day: 'Common.Day',
        days: 'Common.Day.Many',
        week: 'Common.Week',
        weeks: 'Common.Week.Many',
        month: 'Common.Months.Short',
        months: 'Common.Months.Short.Many',
        year: 'Common.Year',
        years: 'Common.Year.Many',
    };

    let result: { amount; label } = null;

    if (
        moment.isMoment(d1) &&
        d1.isValid() &&
        moment.isMoment(d2) &&
        d2.isValid()
    ) {
        let daysDifference: number = Math.abs(d1.diff(d2, MOMENT_UNIT.DAYS));

        if (daysDifference < 1) {
            result = { amount: null, label: keys.today };
        } else if (daysDifference < 2) {
            result = { amount: daysDifference, label: keys.day };
        } else if (daysDifference < 7) {
            result = { amount: daysDifference, label: keys.days };
        } else if (daysDifference < 14) {
            result = { amount: 1, label: keys.week };
        } else if (daysDifference < 21) {
            result = { amount: 2, label: keys.weeks };
        } else if (daysDifference < 28) {
            result = { amount: 3, label: keys.weeks };
        } else if (daysDifference < 58) {
            result = { amount: 1, label: keys.month };
        } else if (daysDifference < 88) {
            result = { amount: 2, label: keys.months };
        } else if (daysDifference < 118) {
            result = { amount: 3, label: keys.months };
        } else if (daysDifference < 148) {
            result = { amount: 4, label: keys.months };
        } else if (daysDifference < 178) {
            result = { amount: 5, label: keys.months };
        } else if (daysDifference < 208) {
            result = { amount: 6, label: keys.months };
        } else if (daysDifference < 238) {
            result = { amount: 7, label: keys.months };
        } else if (daysDifference < 268) {
            result = { amount: 8, label: keys.months };
        } else if (daysDifference < 298) {
            result = { amount: 9, label: keys.months };
        } else if (daysDifference < 328) {
            result = { amount: 10, label: keys.months };
        } else if (daysDifference < 366) {
            result = { amount: 11, label: keys.months };
        } else if (daysDifference < 730) {
            result = { amount: 1, label: keys.year };
        } else {
            result = {
                amount: Math.floor(Math.abs(d1.diff(d2, MOMENT_UNIT.YEARS))),
                label: keys.years,
            };
        }
    }

    return result;
}

export function trimTo12HourFormat(
    time: string,
    includeMeridian: boolean = true
) {
    let result: string = '';

    if (time) {
        let formatLong: string = FORMAT.TIME_DISPLAY_2;
        let formatShort: string = FORMAT.HOUR_DISPLAY;

        const minutesPresent: boolean = time.split(':').pop() !== '00';
        result = moment(time, FORMAT.TIME_SHORT).format(
            minutesPresent ? formatLong : formatShort
        );

        // moment does not support a format to just show 'a' or 'p' so we need to truncate that last character from output which would
        // am/pm
        if (includeMeridian) {
            result = result.slice(0, -1);
        } else {
            result = result.slice(0, -2);
        }
    }

    return result;
}

export function to12HourDurationFormat(
    start: string,
    end: string,
    showAllDayLabel: boolean = true,
    timeFormat: string = FORMAT.TIME_SHORT
): { time: string; allDay: boolean } {
    const DAY_START_TIME: Moment = moment().startOf(MOMENT_UNIT.DAYS);
    const DAY_END_TIME: Moment = moment().endOf(MOMENT_UNIT.DAYS);

    let result: { time; allDay } = {
        time: null,
        allDay: false,
    };

    if (
        showAllDayLabel &&
        moment(start, timeFormat).isSame(DAY_START_TIME, MOMENT_UNIT.MINUTES) &&
        moment(end, timeFormat).isSame(DAY_END_TIME, MOMENT_UNIT.MINUTES)
    ) {
        result.allDay = true;
    } else {
        result.time = `${trimTo12HourFormat(start)}-${trimTo12HourFormat(end)}`;
    }

    return result;
}

export function datetimeStrToUTCDatetimeStr(
    value: string,
    format: string = FORMAT.DATETIME_SERVER
): string {
    const datetimeMoment: Moment = moment(value, format);
    let result: string = value;

    if (datetimeMoment.isValid()) {
        result = datetimeMoment.utc().format(format);
    }

    return result;
}

export function datetimeMomentToUTCDatetimeStr(
    value: Moment,
    format: string = FORMAT.DATETIME_SERVER
): string {
    let result: string;

    if (!!value?.isValid && value.isValid()) {
        result = value.utc().format(format);
    }

    return result;
}
